import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, CloudUpload, Rocket, Sparkles, Wrench, Workflow, Boxes } from "lucide-react";
import useHashScroll from "@/lib/useHashScroll";
import { scrollToHash } from "@/lib/scroll";
import { EASE, Eyebrow, Reveal, fadeUp, staggerContainer } from "@/components/site/motion";

const solutionDetails = [
  {
    slug: "startups",
    label: "For Startups",
    icon: Rocket,
    heading: "Turn an idea into a product that can grow.",
    copy: [
      "Bytevora helps startups move from early-stage concepts to practical, scalable digital products.",
      "We can support the technical journey from initial architecture and MVP development through integrations, infrastructure, deployment, and future product growth.",
    ],
    needs: ["MVP development", "SaaS product development", "Web applications", "Mobile applications", "Technical architecture", "APIs and integrations", "Cloud deployment", "Product modernization", "Technical consulting"],
    statement: "The goal is not simply to launch quickly — it is to create a technical foundation that can evolve as the product and business grow.",
    cta: "Explore Startup Solutions",
    variant: "startups",
  },
  {
    slug: "saas",
    label: "SaaS",
    icon: Boxes,
    heading: "Build software people can actually depend on.",
    copy: [
      "Bytevora helps businesses design and engineer SaaS products with scalable architecture, clear user experiences, secure backend systems, integrations, billing workflows, and cloud infrastructure.",
    ],
    needs: ["SaaS architecture", "Multi-tenant systems", "User authentication", "Subscription workflows", "Admin platforms", "API development", "Payment integration", "Analytics", "Cloud deployment", "Ongoing product development"],
    statement: "From an initial SaaS idea to an existing platform that needs to scale, we focus on maintainability, reliability, and long-term product evolution.",
    cta: "Explore SaaS Development",
    variant: "saas",
  },
  {
    slug: "business-automation",
    label: "Automation",
    icon: Workflow,
    heading: "Replace repetitive processes with systems that work together.",
    copy: [
      "Manual processes, disconnected tools, repeated data entry, and fragmented workflows can limit growth.",
      "Bytevora designs automation solutions that connect systems, reduce repetitive work, and improve operational consistency.",
    ],
    needs: ["Workflow automation", "CRM automation", "Data synchronization", "API integrations", "Internal tools", "Approval workflows", "Notifications", "Reporting automation", "Billing workflows", "Operational dashboards"],
    statement: "Automation should reduce complexity — not create another layer of it.",
    cta: "Explore Business Automation",
    variant: "automation",
  },
  {
    slug: "ai-integration",
    label: "AI",
    icon: Sparkles,
    heading: "Put AI where it creates practical value.",
    copy: [
      "Bytevora helps businesses integrate AI into products and workflows where it can improve efficiency, decision support, user experience, or automation.",
      "Rather than adding AI for the sake of it, we focus on use cases where the technology can solve a clear problem.",
    ],
    needs: ["AI-powered search", "Intelligent assistants", "Content processing", "Data extraction", "Classification", "Workflow automation", "Recommendation systems", "Document analysis", "AI-enabled product features", "Internal productivity tools"],
    statement: "AI systems may involve third-party models, APIs, proprietary workflows, or custom data pipelines depending on the requirement.",
    cta: "Explore AI Integration",
    variant: "ai",
  },
  {
    slug: "cloud-modernization",
    label: "Cloud",
    icon: CloudUpload,
    heading: "Build infrastructure that supports the next stage of growth.",
    copy: [
      "Bytevora helps businesses improve how applications are deployed, hosted, scaled, and maintained.",
      "Whether moving from a basic server setup or improving an existing cloud environment, we focus on reliability, deployment efficiency, performance, maintainability, and operational visibility.",
    ],
    needs: ["Cloud migration", "Application deployment", "Server modernization", "Containerization", "CI/CD", "Environment management", "Performance optimization", "Infrastructure architecture", "Monitoring", "Backup strategy", "Scaling planning"],
    statement: null,
    cta: "Explore Cloud Modernization",
    variant: "cloud",
  },
  {
    slug: "system-modernization",
    label: "Modernization",
    icon: Wrench,
    heading: "Improve the systems your business already depends on.",
    copy: [
      "Not every technology project starts from zero.",
      "Bytevora helps businesses improve existing applications, modernize legacy systems, replace fragile processes, connect disconnected platforms, and prepare older technology for future growth.",
    ],
    needs: ["Legacy application modernization", "Architecture improvement", "API development", "Platform migration", "System integration", "Performance improvement", "Workflow digitization", "UI modernization", "Database modernization", "Technical debt reduction"],
    statement: "The objective is to preserve what still works while improving the parts that limit reliability, usability, or scalability.",
    cta: "Explore System Modernization",
    variant: "modernization",
  },
];

const approachStages = [
  { num: "01", title: "Understand", desc: "We begin with the business objective, existing environment, constraints, users, and technical requirements." },
  { num: "02", title: "Shape", desc: "We define the solution, architecture, priorities, integrations, and implementation approach." },
  { num: "03", title: "Build", desc: "We engineer the product or system in focused stages, with testing and feedback throughout development." },
  { num: "04", title: "Evolve", desc: "We support deployment, improvement, scaling, modernization, and future development where required." },
];

const audiences = [
  { title: "Startups", desc: "Turn an idea into a technically sound MVP or scalable product foundation." },
  { title: "Growing Businesses", desc: "Replace manual processes, connect systems, and improve operational efficiency." },
  { title: "Established Companies", desc: "Modernize software, infrastructure, and internal technology without starting from scratch." },
  { title: "Product Teams", desc: "Extend engineering capability, modernize architecture, integrate new technologies, or accelerate delivery." },
];

const whyPoints = [
  { title: "Problem First", desc: "We begin with what needs to be solved before deciding which technology should be used." },
  { title: "Scalable Foundations", desc: "We consider how the solution will operate, evolve, and integrate beyond the first release." },
  { title: "Practical Engineering", desc: "Architecture and technology choices should support real requirements rather than unnecessary complexity." },
  { title: "Long-Term Thinking", desc: "We build with maintainability, reliability, and future change in mind." },
];

const startingPoints = [
  { quote: "We have an idea but need to turn it into a product.", target: "Startup / SaaS Solution", hash: "#startups" },
  { quote: "Our team spends too much time doing repetitive work.", target: "Business Automation", hash: "#business-automation" },
  { quote: "We want to add AI to our product or workflow.", target: "AI Integration", hash: "#ai-integration" },
  { quote: "Our current system is becoming difficult to maintain.", target: "System Modernization", hash: "#system-modernization" },
  { quote: "Our infrastructure cannot support growth efficiently.", target: "Cloud Modernization", hash: "#cloud-modernization" },
];

function SolutionVisual({ variant }) {
  const gid = `sov-${variant}`;
  const stroke = `url(#${gid})`;
  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_30px_60px_-40px_rgba(11,18,32,0.25)]">
      <div className="bg-grid-light absolute inset-0 opacity-50" aria-hidden="true" />
      <svg viewBox="0 0 480 320" className="relative block h-auto w-full" role="img" aria-label={`Abstract ${variant} diagram`}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#2563EB" />
            <stop offset="100%" stopColor="#7C3AED" />
          </linearGradient>
        </defs>

        {variant === "startups" && (() => {
          const pts = [["idea", 60, 90], ["mvp", 150, 130], ["launch", 240, 90], ["scale", 330, 50]];
          return (
            <g>
              <line x1="50" y1="250" x2="430" y2="250" stroke="#E2E8F0" strokeWidth="1.5" />
              {pts.map(([l, x, y], i) => (
                <g key={l}>
                  {i > 0 && (
                    <line x1={pts[i - 1][1] + 70} y1={pts[i - 1][2] + 17} x2={x} y2={y + 17} stroke={stroke} strokeWidth="1.4" opacity="0.5" className={i % 2 ? "dash-flow" : ""} />
                  )}
                  <rect x={x} y={y} width="70" height="34" rx="9" fill="#fff" stroke={stroke} strokeWidth="1.5" />
                  <text x={x + 35} y={y + 21} fontSize="10" fill="#64748B" fontFamily="'Geist Mono', monospace" letterSpacing="1" textAnchor="middle">{l}</text>
                  <line x1={x + 35} y1={y + 34} x2={x + 35} y2="250" stroke="#E2E8F0" strokeWidth="1" strokeDasharray="3 5" />
                </g>
              ))}
              <circle cx="435" cy="67" r="5" fill="none" stroke={stroke} strokeWidth="1.5" className="node-pulse" />
              <circle cx="435" cy="67" r="4" fill={stroke} />
            </g>
          );
        })()}

        {variant === "saas" && (
          <g>
            {[["ui", 40], ["api", 108], ["core", 176], ["data", 244]].map(([l, y], i) => (
              <g key={l}>
                <rect x="90" y={y} width="300" height="48" rx="12" fill={i === 1 ? stroke : "#fff"} stroke={stroke} strokeWidth="1.5" opacity={i === 1 ? 0.92 : 1} />
                <text x="240" y={y + 29} fontSize="11" fill={i === 1 ? "#fff" : "#64748B"} fontFamily="'Geist Mono', monospace" letterSpacing="1.5" textAnchor="middle">{l}</text>
                {i < 3 && <line x1="240" y1={y + 48} x2="240" y2={y + 68} stroke={stroke} strokeWidth="1.3" opacity="0.55" className="dash-flow" />}
                <circle cx="110" cy={y + 24} r="3.5" fill={i === 1 ? "#fff" : "#CBD5E1"} />
                <circle cx="370" cy={y + 24} r="3.5" fill={i === 1 ? "#fff" : "#CBD5E1"} />
              </g>
            ))}
          </g>
        )}

        {variant === "automation" && (
          <g>
            <line x1="96" y1="160" x2="196" y2="80" stroke={stroke} strokeWidth="1.4" opacity="0.5" className="dash-flow" />
            <line x1="96" y1="160" x2="196" y2="240" stroke={stroke} strokeWidth="1.4" opacity="0.5" className="dash-flow" />
            <line x1="248" y1="80" x2="356" y2="160" stroke={stroke} strokeWidth="1.4" opacity="0.5" />
            <line x1="248" y1="240" x2="356" y2="160" stroke={stroke} strokeWidth="1.4" opacity="0.5" />
            <rect x="40" y="140" width="56" height="40" rx="10" fill="#fff" stroke={stroke} strokeWidth="1.5" />
            <text x="68" y="164" fontSize="9" fill="#64748B" fontFamily="'Geist Mono', monospace" textAnchor="middle">input</text>
            <rect x="196" y="56" width="52" height="34" rx="9" fill="#EFF6FF" stroke={stroke} strokeWidth="1.2" />
            <text x="222" y="76" fontSize="9" fill="#64748B" fontFamily="'Geist Mono', monospace" textAnchor="middle">crm</text>
            <rect x="196" y="226" width="52" height="34" rx="9" fill="#F5F3FF" stroke={stroke} strokeWidth="1.2" />
            <text x="222" y="246" fontSize="9" fill="#64748B" fontFamily="'Geist Mono', monospace" textAnchor="middle">data</text>
            <circle cx="384" cy="160" r="26" fill="none" stroke={stroke} strokeWidth="1.5" />
            <circle cx="384" cy="160" r="26" fill="none" stroke={stroke} strokeWidth="1.5" className="node-pulse" />
            <circle cx="384" cy="160" r="7" fill={stroke} />
            <text x="384" y="206" fontSize="9" fill="#94A3B8" fontFamily="'Geist Mono', monospace" letterSpacing="1.5" textAnchor="middle">automated</text>
          </g>
        )}

        {variant === "ai" && (
          <g>
            {[["data", 60], ["model", 205], ["output", 360]].map(([l, x], i) => (
              <g key={l}>
                {i < 2 && (
                  <g>
                    <line x1={x + 60} y1="160" x2={x + 145} y2="160" stroke={stroke} strokeWidth="1.4" opacity="0.55" className="dash-flow" />
                    <path d={`M${x + 139} 155 l9 5 -9 5`} fill="none" stroke={stroke} strokeWidth="1.6" />
                  </g>
                )}
                {i === 1 ? (
                  <g>
                    <circle cx={x + 30} cy="160" r="30" fill="none" stroke={stroke} strokeWidth="1.5" />
                    <circle cx={x + 30} cy="160" r="30" fill="none" stroke={stroke} strokeWidth="1.5" className="node-pulse" />
                    <circle cx={x + 22} cy="152" r="4" fill={stroke} />
                    <circle cx={x + 40} cy="158" r="4" fill={stroke} />
                    <circle cx={x + 26} cy="170" r="4" fill={stroke} />
                    <line x1={x + 22} y1="152" x2={x + 40} y2="158" stroke={stroke} strokeWidth="1" />
                    <line x1={x + 40} y1="158" x2={x + 26} y2="170" stroke={stroke} strokeWidth="1" />
                  </g>
                ) : (
                  <rect x={x} y="138" width="60" height="44" rx="11" fill={i === 2 ? stroke : "#fff"} stroke={stroke} strokeWidth="1.5" opacity={i === 2 ? 0.92 : 1} />
                )}
                <text x={x + 30} y={i === 1 ? 208 : 165} fontSize="10" fill={i === 2 ? "#fff" : "#64748B"} fontFamily="'Geist Mono', monospace" letterSpacing="1" textAnchor="middle">{l}</text>
              </g>
            ))}
          </g>
        )}

        {variant === "cloud" && (
          <g>
            <circle cx="200" cy="84" r="30" fill="#fff" stroke={stroke} strokeWidth="1.5" />
            <circle cx="248" cy="72" r="38" fill="#fff" stroke={stroke} strokeWidth="1.5" />
            <circle cx="292" cy="90" r="26" fill="#fff" stroke={stroke} strokeWidth="1.5" />
            <rect x="170" y="86" width="150" height="30" rx="15" fill="#fff" />
            {[["deploy", 150], ["monitor", 240], ["scale", 330]].map(([l, x]) => (
              <g key={l}>
                <line x1={x - 8} y1="122" x2={x - 8} y2="176" stroke={stroke} strokeWidth="1.3" opacity="0.5" className="dash-flow" />
                <rect x={x - 48} y="176" width="80" height="34" rx="10" fill="#fff" stroke={stroke} strokeWidth="1.4" />
                <text x={x - 8} y="197" fontSize="10" fill="#64748B" fontFamily="'Geist Mono', monospace" letterSpacing="1" textAnchor="middle">{l}</text>
              </g>
            ))}
            <rect x="110" y="240" width="260" height="36" rx="10" fill={stroke} opacity="0.92" />
            <text x="240" y="262" fontSize="10" fill="#fff" fontFamily="'Geist Mono', monospace" letterSpacing="1.5" textAnchor="middle">modern infrastructure</text>
          </g>
        )}

        {variant === "modernization" && (
          <g>
            <rect x="50" y="120" width="130" height="80" rx="12" fill="#fff" stroke="#CBD5E1" strokeWidth="1.5" strokeDasharray="6 5" />
            <text x="115" y="158" fontSize="10" fill="#94A3B8" fontFamily="'Geist Mono', monospace" letterSpacing="1" textAnchor="middle">legacy</text>
            <text x="115" y="176" fontSize="10" fill="#94A3B8" fontFamily="'Geist Mono', monospace" letterSpacing="1" textAnchor="middle">system</text>
            <line x1="196" y1="160" x2="262" y2="160" stroke={stroke} strokeWidth="1.5" className="dash-flow" />
            <path d="M256 154 l10 6 -10 6" fill="none" stroke={stroke} strokeWidth="1.8" />
            <circle cx="236" cy="160" r="8" fill="#fff" stroke={stroke} strokeWidth="2" />
            <rect x="286" y="96" width="150" height="40" rx="10" fill="#fff" stroke={stroke} strokeWidth="1.4" />
            <rect x="286" y="148" width="150" height="40" rx="10" fill="#fff" stroke={stroke} strokeWidth="1.4" />
            <rect x="286" y="200" width="150" height="40" rx="10" fill={stroke} opacity="0.92" />
            <text x="361" y="120" fontSize="10" fill="#64748B" fontFamily="'Geist Mono', monospace" letterSpacing="1" textAnchor="middle">modern ui</text>
            <text x="361" y="172" fontSize="10" fill="#64748B" fontFamily="'Geist Mono', monospace" letterSpacing="1" textAnchor="middle">apis</text>
            <text x="361" y="224" fontSize="10" fill="#fff" fontFamily="'Geist Mono', monospace" letterSpacing="1" textAnchor="middle">modern core</text>
          </g>
        )}
      </svg>
    </div>
  );
}

function HeroFlowVisual() {
  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200 bg-white/85 shadow-[0_40px_80px_-45px_rgba(11,18,32,0.35)] backdrop-blur">
      <div className="bg-grid-light absolute inset-0 opacity-60" aria-hidden="true" />
      <div className="relative flex items-center gap-2 border-b border-slate-100 px-5 py-3.5">
        <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
        <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
          solution.pipeline
        </span>
      </div>
      <svg viewBox="0 0 480 340" className="relative block h-auto w-full" role="img" aria-label="Business need to scale flow diagram">
        <defs>
          <linearGradient id="hero-flow" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#2563EB" />
            <stop offset="100%" stopColor="#7C3AED" />
          </linearGradient>
        </defs>
        {[["business need", 30], ["strategy", 100], ["technology", 170], ["build", 240], ["scale", 310]].map(([l, y], i) => (
          <g key={l}>
            {i < 4 && (
              <g>
                <line x1="240" y1={y + 40} x2="240" y2={y + 70} stroke="url(#hero-flow)" strokeWidth="1.4" opacity="0.6" className="dash-flow" />
                <path d={`M236 ${y + 64} l4 8 4-8`} fill="none" stroke="url(#hero-flow)" strokeWidth="1.6" />
              </g>
            )}
            <rect x="150" y={y} width="180" height="40" rx="11" fill={i === 4 ? "url(#hero-flow)" : "#fff"} stroke="url(#hero-flow)" strokeWidth="1.4" opacity={i === 4 ? 0.95 : 1} />
            <text x="240" y={y + 25} fontSize="10.5" fill={i === 4 ? "#fff" : "#64748B"} fontFamily="'Geist Mono', monospace" letterSpacing="1.5" textAnchor="middle">{l}</text>
            <circle cx="132" cy={y + 20} r="4" fill="#fff" stroke="url(#hero-flow)" strokeWidth="1.6" />
            <circle cx="348" cy={y + 20} r="4" fill="#fff" stroke="url(#hero-flow)" strokeWidth="1.6" />
          </g>
        ))}
      </svg>
    </div>
  );
}

function CrossCapabilityVisual() {
  const center = { x: 240, y: 170 };
  const caps = [
    { label: "Software Engineering", x: 240, y: 42 },
    { label: "Web & Mobile", x: 52, y: 130 },
    { label: "AI & Automation", x: 428, y: 130 },
    { label: "Cloud", x: 90, y: 292 },
    { label: "Consulting", x: 390, y: 292 },
  ];
  return (
    <div className="relative mx-auto max-w-[620px] overflow-hidden rounded-[26px] border border-white/10 bg-navy-800/60 p-2">
      <div className="bg-grid-dark absolute inset-0" aria-hidden="true" />
      <svg viewBox="0 0 480 340" className="relative block h-auto w-full" role="img" aria-label="Capabilities converging into a solution">
        <defs>
          <linearGradient id="cap-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#60A5FA" />
            <stop offset="100%" stopColor="#A78BFA" />
          </linearGradient>
        </defs>
        {caps.map((c, i) => (
          <line key={c.label} x1={c.x} y1={c.y} x2={center.x} y2={center.y} stroke="url(#cap-grad)" strokeWidth="1.2" opacity="0.5" className={i % 2 === 0 ? "dash-flow" : ""} />
        ))}
        <circle cx={center.x} cy={center.y} r="46" fill="none" stroke="url(#cap-grad)" strokeWidth="1.4" className="node-pulse" />
        <circle cx={center.x} cy={center.y} r="44" fill="#0B1220" stroke="url(#cap-grad)" strokeWidth="1.6" />
        <text x={center.x} y={center.y + 4} fontSize="10" fill="#fff" fontFamily="'Geist Mono', monospace" letterSpacing="2" textAnchor="middle">SOLUTION</text>
        {caps.map((c) => (
          <g key={c.label}>
            <circle cx={c.x} cy={c.y} r="5" fill="#0B1220" stroke="url(#cap-grad)" strokeWidth="1.8" />
            <text x={c.x} y={c.y + (c.y > center.y ? 24 : -14)} fontSize="10" fill="#94A3B8" fontFamily="'Geist Mono', monospace" letterSpacing="1" textAnchor="middle">
              {c.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

export default function SolutionsPage() {
  useHashScroll();

  return (
    <main className="bg-white" data-testid="solutions-page">
      {/* Hero */}
      <section className="relative overflow-hidden pt-[72px]">
        <div className="bg-grid-light absolute inset-0" aria-hidden="true" />
        <div className="absolute -right-40 top-[-120px] h-[420px] w-[420px] rounded-full bg-violet-600/10 blur-[120px]" aria-hidden="true" />
        <div className="relative mx-auto grid w-full max-w-[1200px] items-center gap-14 px-6 py-20 md:px-8 md:py-28 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
              <Eyebrow>Solutions</Eyebrow>
            </motion.div>
            <motion.h1
              data-testid="solutions-hero-heading"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.95, ease: EASE, delay: 0.12 }}
              className="mt-6 text-[clamp(2.5rem,5vw,4.2rem)] font-extrabold leading-[1.03] tracking-[-0.03em] text-slate-900"
            >
              Technology shaped around{" "}
              <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
                the problem.
              </span>
            </motion.h1>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.28 }}
            >
              <p className="mt-6 max-w-[540px] text-base leading-relaxed text-slate-600 md:text-lg">
                Bytevora helps businesses turn product ideas, operational challenges, legacy
                systems, and growth requirements into practical technology solutions built for
                long-term use.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  data-testid="solutions-start-project-button"
                  className="group inline-flex items-center gap-2.5 rounded-[10px] bg-navy px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-blue-600"
                >
                  Start a Project
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/services"
                  data-testid="solutions-explore-services-link"
                  className="group inline-flex items-center gap-2 px-2 py-3.5 text-sm font-semibold text-slate-900 transition-colors duration-300 hover:text-blue-600"
                >
                  Explore Our Services
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.35 }}
            className="hidden lg:col-span-5 lg:block"
          >
            <HeroFlowVisual />
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="bg-[#F8FAFC] py-24 md:py-28" data-testid="solutions-overview">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-8">
          <Reveal className="max-w-2xl">
            <h2 className="text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.5rem]">
              Start with the outcome, not the technology.
            </h2>
            <p className="mt-5 text-[15px] leading-[1.8] text-slate-600 md:text-base">
              Every business challenge has a different technical path. Bytevora combines the right
              capabilities across software engineering, cloud, AI, automation, integration, and
              consulting to build solutions around the actual problem — rather than forcing every
              project into the same template.
            </p>
          </Reveal>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3"
          >
            {solutionDetails.map((s) => (
              <motion.button
                key={s.slug}
                type="button"
                variants={fadeUp}
                data-testid={`solutions-overview-${s.slug}`}
                onClick={() => scrollToHash(`#${s.slug}`)}
                className="group flex h-full flex-col rounded-[18px] border border-slate-200 bg-white p-7 text-left transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-0.5 hover:border-blue-600/40 hover:shadow-[0_28px_55px_-32px_rgba(37,99,235,0.4)]"
              >
                <span className="grid h-11 w-11 place-items-center rounded-xl border border-slate-200 bg-slate-50 text-violet-600">
                  <s.icon size={19} strokeWidth={1.7} />
                </span>
                <h3 className="mt-6 text-lg font-semibold tracking-tight text-slate-900">{s.heading.split(".")[0]}.</h3>
                <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate-600">{s.label} — {s.statement ? s.statement.split(".")[0] : s.copy[0].split(".")[0]}.</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 transition-colors duration-300 group-hover:text-blue-600">
                  Explore
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </motion.button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Detailed solution blocks */}
      {solutionDetails.map((s, i) => (
        <section
          key={s.slug}
          id={s.slug}
          data-testid={`solution-detail-${s.slug}`}
          className={`scroll-mt-28 py-24 md:py-28 ${i % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}`}
        >
          <div className="mx-auto grid w-full max-w-[1200px] items-center gap-14 px-6 md:px-8 lg:grid-cols-2">
            <Reveal className={i % 2 === 1 ? "lg:order-2" : ""}>
              <p className="inline-flex items-center gap-2.5 rounded-full border border-slate-200 bg-white px-4 py-1.5 font-mono text-[10.5px] uppercase tracking-[0.24em] text-slate-500">
                <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-blue-600 to-violet-600" />
                {s.label}
              </p>
              <h2 className="mt-6 text-3xl font-bold leading-[1.12] tracking-[-0.02em] text-slate-900 md:text-[2.3rem]">
                {s.heading}
              </h2>
              {s.copy.map((p, ci) => (
                <p key={ci} className="mt-4 max-w-lg text-[15px] leading-[1.8] text-slate-600">
                  {p}
                </p>
              ))}
              <div className="mt-8 flex flex-wrap gap-2">
                {s.needs.map((n) => (
                  <span key={n} className="rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-[12.5px] font-medium text-slate-600">
                    {n}
                  </span>
                ))}
              </div>
              {s.statement && (
                <p className="mt-8 border-l-2 border-blue-600 pl-5 text-[15px] font-medium leading-relaxed text-slate-800">
                  {s.statement}
                </p>
              )}
              <Link
                to="/contact"
                data-testid={`solution-cta-${s.slug}`}
                className="group mt-9 inline-flex items-center gap-2 text-sm font-semibold text-slate-900 transition-colors duration-300 hover:text-blue-600"
              >
                Discuss This Solution
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
            <Reveal delay={1} className={i % 2 === 1 ? "lg:order-1" : ""}>
              <SolutionVisual variant={s.variant} />
            </Reveal>
          </div>
        </section>
      ))}

      {/* Approach */}
      <section className="bg-white py-24 md:py-28" data-testid="solutions-approach">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>Our approach</Eyebrow>
            <h2 className="mt-5 text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.4rem]">
              The right solution starts with understanding the problem.
            </h2>
            <p className="mt-5 text-[15px] leading-[1.8] text-slate-600 md:text-base">
              Technology choices should follow the business requirement, not the other way around.
            </p>
          </Reveal>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className="relative mt-16 grid gap-12 md:grid-cols-4 md:gap-8"
          >
            <div className="absolute left-0 right-0 top-6 hidden border-t border-dashed border-slate-200 md:block" aria-hidden="true" />
            {approachStages.map((s) => (
              <motion.div key={s.num} variants={fadeUp} data-testid={`approach-${s.num}`} className="group relative">
                <div className="relative z-10 grid h-12 w-12 place-items-center rounded-full border border-slate-200 bg-white font-mono text-[13px] tracking-[0.1em] text-slate-500 transition-[border-color,color,box-shadow] duration-300 group-hover:border-violet-600/50 group-hover:text-violet-600 group-hover:shadow-[0_0_0_6px_rgba(124,58,237,0.08)]">
                  {s.num}
                </div>
                <h3 className="mt-6 text-lg font-semibold tracking-tight text-slate-900">{s.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Cross-capability — dark */}
      <section className="relative overflow-hidden bg-navy py-24 text-white md:py-32" data-testid="solutions-cross-capability">
        <div className="bg-grid-dark absolute inset-0" aria-hidden="true" />
        <div className="absolute -left-40 top-[-140px] h-[420px] w-[420px] rounded-full bg-blue-600/20 blur-[130px]" aria-hidden="true" />
        <div className="relative mx-auto w-full max-w-[1200px] px-6 md:px-8">
          <Reveal className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-white md:text-[2.4rem]">
              One solution can bring together multiple capabilities.
            </h2>
            <p className="mt-5 text-[15px] leading-[1.8] text-slate-400">
              A SaaS product may require software engineering, cloud infrastructure, payment
              integration, automation, data architecture, and consulting. A business automation
              project may require APIs, internal tools, AI, cloud services, and system integration.
              Bytevora combines capabilities around the project rather than treating each service
              as an isolated offering.
            </p>
          </Reveal>
          <Reveal delay={1} className="mt-14">
            <CrossCapabilityVisual />
          </Reveal>
        </div>
      </section>

      {/* Who for */}
      <section className="bg-white py-24 md:py-28" data-testid="solutions-audiences">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-8">
          <Reveal className="max-w-xl">
            <h2 className="text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.4rem]">
              Built for businesses at different stages.
            </h2>
          </Reveal>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
          >
            {audiences.map((a, i) => (
              <motion.div
                key={a.title}
                variants={fadeUp}
                data-testid={`solutions-audience-${i}`}
                className="group rounded-[18px] border border-slate-200 bg-white p-7 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-violet-600/35"
              >
                <span className="font-mono text-xs tracking-[0.18em] text-slate-300 transition-colors duration-300 group-hover:text-violet-600">
                  0{i + 1}
                </span>
                <h3 className="mt-4 text-[17px] font-semibold tracking-tight text-slate-900">{a.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{a.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Bytevora */}
      <section className="bg-[#F8FAFC] py-24 md:py-28" data-testid="solutions-why">
        <div className="mx-auto grid w-full max-w-[1200px] gap-12 px-6 md:px-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Eyebrow>Why Bytevora</Eyebrow>
            <h2 className="mt-5 text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.4rem]">
              Engineering around the outcome — not the buzzword.
            </h2>
          </Reveal>
          <div className="grid gap-8 sm:grid-cols-2 lg:col-span-7">
            {whyPoints.map((p, i) => (
              <Reveal key={p.title} delay={i}>
                <span className="font-mono text-xs tracking-[0.18em] text-slate-300">0{i + 1}</span>
                <h3 className="mt-3 text-[16px] font-semibold tracking-tight text-slate-900">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Common starting points */}
      <section className="bg-white py-24 md:py-28" data-testid="solutions-starting-points">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-8">
          <Reveal className="max-w-2xl">
            <h2 className="text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.4rem]">
              You may not need a service. You may need an answer to a problem.
            </h2>
          </Reveal>
          <div className="mt-14 flex flex-col gap-3">
            {startingPoints.map((sp, i) => (
              <Reveal key={sp.quote} delay={Math.min(i, 2)}>
                <button
                  type="button"
                  data-testid={`starting-point-${i}`}
                  onClick={() => scrollToHash(sp.hash)}
                  className="group flex w-full flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-6 text-left transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-blue-600/35 md:flex-row md:items-center md:justify-between md:p-7"
                >
                  <span className="max-w-xl text-[16px] font-semibold leading-snug tracking-tight text-slate-900">
                    &ldquo;{sp.quote}&rdquo;
                  </span>
                  <span className="inline-flex shrink-0 items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-blue-600">
                    {sp.target}
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-navy py-24 md:py-32" data-testid="solutions-cta">
        <div className="bg-grid-dark absolute inset-0" aria-hidden="true" />
        <div className="absolute -right-40 bottom-[-140px] h-[420px] w-[420px] rounded-full bg-violet-600/25 blur-[130px]" aria-hidden="true" />
        <div className="relative mx-auto max-w-[860px] px-6 text-center md:px-8">
          <Reveal>
            <h2 className="text-4xl font-extrabold leading-[1.05] tracking-[-0.03em] text-white sm:text-5xl">
              Have a challenge that doesn&rsquo;t fit{" "}
              <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
                neatly into one category?
              </span>
            </h2>
          </Reveal>
          <Reveal delay={1}>
            <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-slate-400 md:text-base">
              Tell us what you are trying to launch, improve, automate, integrate, or scale.
              We&rsquo;ll help identify the technical direction that makes sense for your situation.
            </p>
          </Reveal>
          <Reveal delay={2} className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              data-testid="solutions-cta-start-project"
              className="group inline-flex items-center gap-2.5 rounded-[10px] bg-white px-7 py-3.5 text-sm font-semibold text-navy transition-colors duration-300 hover:bg-blue-600 hover:text-white"
            >
              Start a Project
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              data-testid="solutions-cta-contact"
              className="group inline-flex items-center gap-2 px-2 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:text-blue-300"
            >
              Contact Bytevora
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
